import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, X, TrendingUp, Activity, Trophy } from 'lucide-react';

/**
 * IncompleteProfileHero — top-of-Dashboard surface shown when
 * `profile.completedOnboarding !== true`.
 *
 * Session 4a: Onboarding is no longer a hard wall. Users land on
 * /progress and see this hero, which:
 *   - Gives them a dominant CTA to complete onboarding (~90s).
 *   - Optionally previews what their dashboard could look like via a dismissible
 *     "Example" tile (honest framing — never presented as real data).
 *
 * The rest of the dashboard renders honestly empty below this — no fake stats,
 * just real zeros and contextual empty-state copy.
 */
const PREVIEW_DISMISSED_KEY = 'enferno_dashboard_preview_dismissed_v1';

const IncompleteProfileHero = ({ firstName }) => {
  const [previewDismissed, setPreviewDismissed] = useState(false);

  useEffect(() => {
    try {
      setPreviewDismissed(localStorage.getItem(PREVIEW_DISMISSED_KEY) === '1');
    } catch { /* ignore */ }
  }, []);

  const dismissPreview = () => {
    setPreviewDismissed(true);
    try { localStorage.setItem(PREVIEW_DISMISSED_KEY, '1'); } catch { /* ignore */ }
  };

  return (
    <div className="mb-10" data-testid="incomplete-profile-hero">
      {/* Primary CTA card */}
      <div className="relative overflow-hidden rounded-xl border border-enf-accent-fg/40 bg-gradient-to-br from-enf-surface-special via-zinc-950 to-enf-border-special/30 p-7 mb-5">
        <div className="absolute top-0 left-6 right-6 h-px iron-rule" />
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-semibold mb-2 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Get started
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-wide leading-tight mb-2">
              {firstName ? <>Welcome, {firstName}.</> : <>Welcome to Enferno.</>}
            </h2>
            <p className="text-enf-text-secondary text-sm md:text-base leading-relaxed max-w-xl">
              Tell us about your equipment, time, goals, and any injuries — about 90 seconds — and your dashboard fills in with a plan built for you.
            </p>
          </div>
          <div className="flex flex-col items-stretch md:items-end gap-2 shrink-0">
            <Link
              to="/onboarding"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors whitespace-nowrap"
              data-testid="incomplete-profile-start-onboarding"
            >
              Start setup <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/onboarding/full"
              className="text-xs text-enf-text-secondary hover:text-enf-role-info transition-colors text-center md:text-right"
              data-testid="incomplete-profile-full-onboarding"
            >
              Or take the full setup &rarr;
            </Link>
          </div>
        </div>
      </div>

      {/* Dismissible preview tile — "Example" framing. Honest about being a mock. */}
      {!previewDismissed && (
        <div
          className="relative rounded-xl border border-dashed border-enf-surface-border bg-enf-surface-deep/60 p-5"
          data-testid="incomplete-profile-preview-tile"
        >
          <button
            type="button"
            onClick={dismissPreview}
            className="absolute top-3 right-3 text-enf-text-muted hover:text-enf-text-primary p-1 transition-colors"
            aria-label="Dismiss preview"
            data-testid="incomplete-profile-preview-dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-[0.25em] text-enf-text-muted font-bold border border-enf-surface-border rounded px-2 py-0.5">Example</span>
            <span className="text-xs text-enf-text-muted">Here&apos;s what your dashboard could look like.</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 opacity-70 select-none pointer-events-none">
            <MockStat icon={TrendingUp} label="This week" value="4 of 7" sub="Pattern" />
            <MockStat icon={Activity} label="Total volume" value="38,420 lb" sub="Last 7 days" />
            <MockStat icon={Trophy} label="Sessions" value="12" sub="Last 30 days" />
          </div>
        </div>
      )}
    </div>
  );
};

const MockStat = ({ icon: Icon, label, value, sub }) => (
  <div className="relative bg-enf-surface-deep border border-enf-surface-border rounded-xl p-4 overflow-hidden">
    <div className="flex items-center justify-between mb-2">
      <div className="w-8 h-8 rounded-md bg-enf-surface-mid flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-enf-accent-fg" />
      </div>
      <span className="text-[10px] text-enf-text-muted">{sub}</span>
    </div>
    <div className="font-display text-2xl tracking-wide leading-none">{value}</div>
    <div className="text-[10px] text-enf-text-muted uppercase tracking-wider mt-1.5">{label}</div>
  </div>
);

export default IncompleteProfileHero;
